import MojitoCore from 'mojito/core';
import { selectSport } from './selectors.js';

import { selectItems as selectContainerItems } from 'services/sports-content/container-items/selectors.js';
const configRegistry = MojitoCore.Services.Config.configRegistry;
const configFactory = MojitoCore.Services.Config.configFactory;

/**
 * Sport data model utilities.
 *
 * @class SportUtils
 * @name utils
 * @memberof Mojito.Services.SportsContent.Sports
 */

const HORSE_RACING = ['horse_racing', 'virtual_horse_racing'];
const GREYHOUND_RACING = ['greyhound_racing', 'virtual_greyhound_racing'];

// Hack to manually register VirtualsEndpoint config as long as getVirtualSport is using it.
// getVirtualSport should be removed from code in favour to normal selection process from Redux store.
import virtualsEndpointDefaultConfig from 'mojito/core/generated/configs/_virtuals-endpoint.js';
configRegistry.add(virtualsEndpointDefaultConfig);

export default class SportUtils {
    /**
     * Determines whether the provided sportId corresponds to horse racing.
     *
     * **Note:** If the sport is available in the Sports store, the 'canonicalType' property will be used, otherwise the 'sportId' will be used.
     *
     * @param {string} sportId - The sportId to verify.
     * @returns {boolean} Returns `true` if the sportId corresponds to horse racing, `false` otherwise.
     * @function Mojito.Services.SportsContent.Sports.utils.isHorseRacing
     */
    static isHorseRacing(sportId) {
        const sport = selectSport(sportId);
        const canonicalType = sport?.canonicalType || sportId;
        return HORSE_RACING.includes(canonicalType);
    }

    /**
     * Checks if the provided sportId corresponds to greyhound racing.
     *
     * **Note:** If the sport is available in the Sports store, the 'canonicalType' property will be used, otherwise the 'sportId' will be used!
     *
     * @param {string} sportId - The sportId to verify.
     * @returns {boolean} Returns true if the sportId corresponds to greyhound racing; otherwise, returns false.
     * @function Mojito.Services.SportsContent.Sports.utils.isGreyhoundRacing
     */
    static isGreyhoundRacing(sportId) {
        const sport = selectSport(sportId);
        const canonicalType = sport?.canonicalType || sportId;
        return GREYHOUND_RACING.includes(canonicalType);
    }

    /**
     * Determines whether the provided sportId corresponds to a racing sport.
     *
     * **Note:** The 'isRacing' property is used to identify racing sports!
     *
     * @param {string} sportId - The sportId to verify.
     * @returns {boolean} Returns `true` if the sportId corresponds to a racing sport, `false` otherwise.
     * @function Mojito.Services.SportsContent.Sports.utils.isRacingSport
     */
    static isRacingSport(sportId) {
        const sport = selectSport(sportId)?.id
            ? selectSport(sportId)
            : SportUtils.getVirtualSport(sportId);

        return Boolean(sport?.isRacing);
    }

    /**
     * Returns virtual sport.
     *
     * @param {string} sportId - Sport ID.
     * @returns {{id: string, name: string, isRacing: boolean, isVirtual: boolean}} Virtual Sport.
     * @function Mojito.Services.SportsContent.Sports.utils.getVirtualSport
     */
    static getVirtualSport(sportId) {
        const { containerId } = configFactory.getConfig('VirtualsEndpoint');
        const virtualsSports = selectContainerItems(containerId);
        return virtualsSports && virtualsSports.find(({ id }) => id === sportId);
    }
}
